import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertTriangle, Clock, TrendingUp, Activity, Zap, BarChart3 } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from 'recharts';

interface ActiveSession {
  evalId: string;
  agentId: string;
  currentItemIndex: number;
  totalItems: number;
  lastFetchedAt: string;
  lastAnsweredAt: string | null;
  currentThinkingTimeMs: number;
  totalPauseTimeMs: number;
}

interface SessionSummary {
  evalId: string;
  agentId: string;
  questionsAnswered: number;
  avgResponseMs: number;
  fastestMs: number;
  slowestMs: number;
  totalPauseMs: number;
  totalSessionMs: number;
  anomalyCount: number;
  fastAnomalies: number;
  slowAnomalies: number;
}

interface SectionPerformance {
  section: string;
  avgTimeMs: number;
  avgBudgetMs: number;
  efficiencyRatio: number;
  questionCount: number;
}

interface TimingAnomaly {
  evalId: string;
  agentId: string;
  itemId: string;
  section: string;
  prompt: string;
  serverMeasuredMs: number;
  ttcMs: number | null;
  timeBudgetSec: number;
  timingAnomaly: string;
  isSuspiciouslyFast: boolean;
  isSuspiciouslySlow: boolean;
  createdAt: string;
}

interface TimingDistribution {
  histogram: { bucket: string; count: number }[];
  anomalySplit: { fast: number; slow: number; normal: number; total: number };
  legacyFlagged: number;
}

export default function EvalTimingAnalytics() {
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [completedSessions, setCompletedSessions] = useState<SessionSummary[]>([]);
  const [sectionPerformance, setSectionPerformance] = useState<SectionPerformance[]>([]);
  const [anomalies, setAnomalies] = useState<TimingAnomaly[]>([]);
  const [distribution, setDistribution] = useState<TimingDistribution | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const asArray = <T,>(payload: unknown): T[] => {
    if (Array.isArray(payload)) return payload;
    if (
      payload &&
      typeof payload === 'object' &&
      'data' in (payload as Record<string, unknown>) &&
      Array.isArray((payload as { data: unknown }).data)
    ) {
      return (payload as { data: T[] }).data;
    }
    return [];
  };

  const fetchData = async () => {
    try {
      const [active, completed, sections, anomaliesData, dist] = await Promise.all([
        api.get('/admin/eval-analytics/active-sessions'),
        api.get('/admin/eval-analytics/session-summaries?limit=20'),
        api.get('/admin/eval-analytics/section-performance'),
        api.get('/admin/eval-analytics/timing-anomalies?limit=50'),
        api.get('/admin/eval-analytics/timing-distribution'),
      ]);

      setActiveSessions(asArray<ActiveSession>(active.data));
      setCompletedSessions(asArray<SessionSummary>(completed.data));
      setSectionPerformance(asArray<SectionPerformance>(sections.data));
      setAnomalies(asArray<TimingAnomaly>(anomaliesData.data));
      setDistribution((dist.data?.data ?? dist.data) as TimingDistribution);
    } catch (error) {
      console.error('Failed to fetch timing analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    if (autoRefresh) {
      const interval = setInterval(fetchData, 5000); // Refresh every 5s
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const formatMs = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  if (loading) {
    return <div className="p-8">Loading timing analytics...</div>;
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Quiz Timing Analytics</h1>
          <p className="text-muted-foreground">
            Server-side performance measurement and anomaly detection
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant={autoRefresh ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setAutoRefresh(!autoRefresh)}>
            <Activity className="h-3 w-3 mr-1" />
            {autoRefresh ? 'Live' : 'Paused'}
          </Badge>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeSessions.length}</div>
            <p className="text-xs text-muted-foreground">Currently in progress</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg Response Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMs(
                Math.round(
                  completedSessions.reduce((sum, s) => sum + s.avgResponseMs, 0) /
                    (completedSessions.length || 1),
                ),
              )}
            </div>
            <p className="text-xs text-muted-foreground">Across all sections</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Anomalies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{anomalies.length}</div>
            <p className="text-xs text-muted-foreground">
              {anomalies.filter(a => a.isSuspiciouslyFast).length} fast, {anomalies.filter(a => a.isSuspiciouslySlow).length} slow
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Fastest Section</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sectionPerformance.length > 0 ? sectionPerformance.reduce((min, s) => s.avgTimeMs < min.avgTimeMs ? s : min).section : '-'}
            </div>
            <p className="text-xs text-muted-foreground">
              {sectionPerformance.length > 0 ? formatMs(sectionPerformance.reduce((min, s) => s.avgTimeMs < min.avgTimeMs ? s : min).avgTimeMs) : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList>
          <TabsTrigger value="active">
            <Activity className="h-4 w-4 mr-2" />
            Active Sessions ({activeSessions.length})
          </TabsTrigger>
          <TabsTrigger value="completed">
            <Clock className="h-4 w-4 mr-2" />
            Completed Sessions
          </TabsTrigger>
          <TabsTrigger value="sections">
            <TrendingUp className="h-4 w-4 mr-2" />
            Section Performance
          </TabsTrigger>
          <TabsTrigger value="anomalies">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Anomalies ({anomalies.length})
          </TabsTrigger>
          <TabsTrigger value="distribution">
            <BarChart3 className="h-4 w-4 mr-2" />
            Distribution
          </TabsTrigger>
        </TabsList>

        {/* Active Sessions */}
        <TabsContent value="active" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Live Session Monitoring</CardTitle>
              <CardDescription>
                Real-time tracking of agents currently taking quizzes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeSessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No active sessions
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent ID</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Current Question</TableHead>
                      <TableHead>Thinking Time</TableHead>
                      <TableHead>Total Pause</TableHead>
                      <TableHead>Last Activity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeSessions.map((session) => (
                      <TableRow key={session.evalId}>
                        <TableCell className="font-mono text-xs">{session.agentId}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {session.currentItemIndex + 1} / {session.totalItems}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={session.currentThinkingTimeMs > 60000 ? 'destructive' : 'default'}>
                            Question {session.currentItemIndex + 1}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span className={session.currentThinkingTimeMs > 120000 ? 'text-red-600 font-semibold' : ''}>
                              {formatMs(session.currentThinkingTimeMs)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{formatMs(session.totalPauseTimeMs)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatTimeAgo(session.lastFetchedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Completed Sessions */}
        <TabsContent value="completed" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Completed Session Performance</CardTitle>
              <CardDescription>
                Timing analysis for recently completed quizzes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent ID</TableHead>
                    <TableHead>Questions</TableHead>
                    <TableHead>Avg Response</TableHead>
                    <TableHead>Range</TableHead>
                    <TableHead>Total Pause</TableHead>
                    <TableHead>Session Duration</TableHead>
                    <TableHead>Anomalies</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {completedSessions.map((session) => (
                    <TableRow key={session.evalId}>
                      <TableCell className="font-mono text-xs">{session.agentId}</TableCell>
                      <TableCell>{session.questionsAnswered}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{formatMs(session.avgResponseMs)}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatMs(session.fastestMs)} - {formatMs(session.slowestMs)}
                      </TableCell>
                      <TableCell>{formatMs(session.totalPauseMs)}</TableCell>
                      <TableCell>{formatMs(session.totalSessionMs)}</TableCell>
                      <TableCell>
                        {session.anomalyCount > 0 ? (
                          <Badge variant="destructive">
                            {session.anomalyCount} ({session.fastAnomalies}F, {session.slowAnomalies}S)
                          </Badge>
                        ) : (
                          <Badge variant="outline">None</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Section Performance */}
        <TabsContent value="sections" className="space-y-4">
          {sectionPerformance.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Avg response time vs. budget by section</CardTitle>
                <CardDescription>
                  Bars near or above the budget line are where agents spend the most time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={sectionPerformance.map((s) => ({
                      section: s.section,
                      avg: Math.round(s.avgTimeMs / 1000),
                      budget: Math.round(s.avgBudgetMs / 1000),
                    }))}
                    margin={{ top: 8, right: 8, bottom: 0, left: -20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.5)" />
                    <XAxis dataKey="section" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} unit="s" />
                    <Tooltip formatter={(v: number, n) => [`${v}s`, n === 'avg' ? 'Avg time' : 'Budget']} />
                    <Bar dataKey="avg" name="avg" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="budget" name="budget" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Performance by Section</CardTitle>
              <CardDescription>
                Average response times and efficiency across different topics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Section</TableHead>
                    <TableHead>Questions</TableHead>
                    <TableHead>Avg Response Time</TableHead>
                    <TableHead>Avg Budget</TableHead>
                    <TableHead>Efficiency</TableHead>
                    <TableHead>Difficulty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sectionPerformance.map((section) => {
                    const efficiency = section.efficiencyRatio;
                    const difficultyLevel = efficiency > 0.8 ? 'Hard' : efficiency > 0.5 ? 'Medium' : 'Easy';
                    const difficultyColor = efficiency > 0.8 ? 'destructive' : efficiency > 0.5 ? 'default' : 'secondary';

                    return (
                      <TableRow key={section.section}>
                        <TableCell className="font-semibold">{section.section}</TableCell>
                        <TableCell>{section.questionCount}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{formatMs(section.avgTimeMs)}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{formatMs(section.avgBudgetMs)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-muted rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${efficiency > 0.8 ? 'bg-red-500' : efficiency > 0.5 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                style={{ width: `${Math.min(efficiency * 100, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs">{(efficiency * 100).toFixed(0)}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={difficultyColor}>{difficultyLevel}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Anomalies */}
        <TabsContent value="anomalies" className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Timing anomalies indicate unusual response patterns. Review flagged entries for potential issues or gaming attempts.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>Timing Anomalies</CardTitle>
              <CardDescription>
                Answers flagged for suspiciously fast/slow response times or client/server time mismatches
              </CardDescription>
            </CardHeader>
            <CardContent>
              {anomalies.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No anomalies detected
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Section</TableHead>
                      <TableHead>Question</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Server Time</TableHead>
                      <TableHead>Client Time</TableHead>
                      <TableHead>Budget</TableHead>
                      <TableHead>Anomaly</TableHead>
                      <TableHead>When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {anomalies.map((anomaly, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{anomaly.agentId}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{anomaly.section}</Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-xs" title={anomaly.prompt}>
                          {anomaly.prompt.slice(0, 50)}...
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {anomaly.isSuspiciouslyFast && (
                              <Badge variant="destructive" className="text-xs">
                                <Zap className="h-3 w-3 mr-1" />
                                Fast
                              </Badge>
                            )}
                            {anomaly.isSuspiciouslySlow && (
                              <Badge variant="destructive" className="text-xs">
                                <Clock className="h-3 w-3 mr-1" />
                                Slow
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold">{formatMs(anomaly.serverMeasuredMs)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {anomaly.ttcMs ? formatMs(anomaly.ttcMs) : 'N/A'}
                        </TableCell>
                        <TableCell>{formatMs(anomaly.timeBudgetSec * 1000)}</TableCell>
                        <TableCell className="text-xs max-w-xs truncate" title={anomaly.timingAnomaly}>
                          {anomaly.timingAnomaly}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {formatTimeAgo(anomaly.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distribution */}
        <TabsContent value="distribution" className="space-y-4">
          {distribution && (() => {
            const current = distribution.anomalySplit.fast + distribution.anomalySplit.slow;
            const legacy = distribution.legacyFlagged;
            if (legacy <= current * 1.5 || legacy === 0) return null;
            return (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{legacy} answers carry a legacy anomaly flag</strong> from an older, far stricter timing
                  rule — but under the current rules only <strong>{current}</strong> would be flagged
                  ({((current / (distribution.anomalySplit.total || 1)) * 100).toFixed(0)}% of {distribution.anomalySplit.total}).
                  The chart below uses the <em>current</em> rules. Historical flags aren’t recomputed on purpose, to
                  preserve past certification integrity scores.
                </AlertDescription>
              </Alert>
            );
          })()}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Response-time distribution</CardTitle>
                <CardDescription>
                  Server-measured (authoritative) response times across all answered questions
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!distribution || distribution.histogram.every((b) => b.count === 0) ? (
                  <div className="text-center py-12 text-muted-foreground">No timing data yet</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={distribution.histogram} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.5)" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v: number) => [`${v} answers`, 'Count']} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {distribution.histogram.map((b, i) => (
                          <Cell key={i} fill={b.bucket === '0-1s' ? '#ef4444' : '#6366f1'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  The 0–1s bucket (red) is the band most likely to trip the fast-anomaly flag.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Anomaly split</CardTitle>
                <CardDescription>Classified under current detection rules</CardDescription>
              </CardHeader>
              <CardContent>
                {!distribution ? (
                  <div className="text-center py-12 text-muted-foreground">—</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={[
                        { label: 'Normal', value: distribution.anomalySplit.normal, fill: '#22c55e' },
                        { label: 'Fast', value: distribution.anomalySplit.fast, fill: '#ef4444' },
                        { label: 'Slow', value: distribution.anomalySplit.slow, fill: '#f59e0b' },
                      ]}
                      margin={{ top: 8, right: 8, bottom: 0, left: -20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.5)" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v: number) => [`${v} answers`, 'Count']} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {['#22c55e', '#ef4444', '#f59e0b'].map((c, i) => (
                          <Cell key={i} fill={c} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
